import type { Metric } from "web-vitals";

export type DeviceClass = "mobile" | "tablet" | "desktop";

export const NEW_RELIC_PAGE_ACTION = "web-vital";

const DEVMODE = process.env.NEXT_PUBLIC_DEVMODE === "1";

/** Aligns with theme breakpoints: md=768, lg=1024. */
export function getDeviceClass(width = getViewportWidth()): DeviceClass {
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

function getViewportWidth(): number {
  if (typeof window === "undefined") return 0;
  return window.innerWidth;
}

export type WebVitalPayload = {
  metric_name: Metric["name"];
  metric_id: string;
  value: number;
  delta: number;
  rating: Metric["rating"];
  navigation_type: Metric["navigationType"];
  page_url: string;
  path: string;
  device_class: DeviceClass;
};

export function buildWebVitalPayload(metric: Metric): WebVitalPayload {
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const path = typeof window !== "undefined" ? window.location.pathname : "";

  return {
    metric_name: metric.name,
    metric_id: metric.id,
    value: metric.name === "CLS" ? Number(metric.value.toFixed(4)) : Math.round(metric.value),
    delta: metric.name === "CLS" ? Number(metric.delta.toFixed(4)) : Math.round(metric.delta),
    rating: metric.rating,
    navigation_type: metric.navigationType,
    page_url: pageUrl,
    path,
    device_class: getDeviceClass(),
  };
}

const AGENT_RETRY_MS = 250;
const AGENT_RETRY_ATTEMPTS = 20;

/** Retries briefly so early metrics (TTFB/FCP) still reach late-loading agents. */
function whenReady(isReady: () => boolean, send: () => void): void {
  if (typeof window === "undefined") return;

  if (isReady()) {
    send();
    return;
  }

  let attempts = 0;
  const intervalId = window.setInterval(() => {
    attempts += 1;
    if (isReady()) {
      window.clearInterval(intervalId);
      send();
      return;
    }
    if (attempts >= AGENT_RETRY_ATTEMPTS) {
      window.clearInterval(intervalId);
    }
  }, AGENT_RETRY_MS);
}

function reportToNewRelic(payload: WebVitalPayload): void {
  whenReady(
    () => typeof window.newrelic?.addPageAction === "function",
    () => {
      window.newrelic?.addPageAction?.(NEW_RELIC_PAGE_ACTION, payload);
    }
  );
}

/**
 * Sends a single web-vitals metric to New Relic.
 * Safe to call repeatedly; retries briefly until the agent is ready.
 */
export function reportWebVital(metric: Metric): void {
  const payload = buildWebVitalPayload(metric);

  if (DEVMODE) {
    // eslint-disable-next-line no-console
    console.info(`[web-vitals] ${payload.metric_name}`, payload);
  }

  reportToNewRelic(payload);
}

/** Registers LCP, INP, CLS, FCP, and TTFB listeners (idempotent per page load). */
export async function initWebVitalsReporting(): Promise<void> {
  if (typeof window === "undefined") return;

  const { onCLS, onFCP, onINP, onLCP, onTTFB } = await import("web-vitals");

  onCLS(reportWebVital);
  onLCP(reportWebVital);
  onFCP(reportWebVital);
  onTTFB(reportWebVital);

  const inpSupported =
    typeof PerformanceEventTiming !== "undefined" &&
    "interactionId" in PerformanceEventTiming.prototype;

  if (!inpSupported) {
    // eslint-disable-next-line no-console
    console.warn(
      "[web-vitals] INP is not supported in this browser (needs Event Timing interactionId). Use Chrome/Edge."
    );
    return;
  }

  /*
   * INP only finalizes on visibility hidden by default, which is easy to miss in
   * an SPA (client navigations do not hide the document) and can lose unload
   * beacons. reportAllChanges sends after the first qualifying interaction.
   * durationThreshold: 0 includes fast interactions the default 40ms filter skips.
   */
  onINP(reportWebVital, { reportAllChanges: true, durationThreshold: 0 });
}
